import { nonEmptyArray } from 'fp-ts';
import pt from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { VectorDirection } from '@mews/enums';
import { ScrollSync } from '@optimus-web/helpers';
import {
    MatrixAside as MatrixAsideComponent,
    MatrixBody as MatrixBodyComponent,
    MatrixHeader as MatrixHeaderComponent,
} from './components';
import { useExpanded } from './hooks';
import { MatrixContentWrapperElement, MatrixWrapperElement } from './styles';

const SCROLL_SYNC_DEBOUNCE_TIMEOUT = 8;

export const matrixFactory = ({
    MatrixAside = MatrixAsideComponent,
    MatrixHeader = MatrixHeaderComponent,
    MatrixBody = MatrixBodyComponent,
    MatrixWrapper = MatrixWrapperElement,
    MatrixContentWrapper = MatrixContentWrapperElement,
} = {}) => {
    const Matrix = ({
        asideCells,
        asideProps,
        asideWidth,
        bodyProps,
        cellHeight,
        cellWidth,
        debounceScrolling,
        debounceTimeout,
        disableScrollSync,
        headerCells,
        headerHeight,
        headerProps,
        matrixHeight,
        matrixWidth,
        preExpandedVectorIndexes,
        vectorDirection,
        vectors,
    }: $TSFixMe) => {
        const [asideHeight, setAsideHeight] = useState(null);
        const [headerWidth, setHeaderWidth] = useState(null);

        const asideRef = useRef(null);
        const bodyRef = useRef(null);
        const headerRef = useRef(null);

        const [expanded, setExpanded] = useExpanded(preExpandedVectorIndexes);

        useEffect(() => {
            const resizeObserver = new window.ResizeObserver((entries) => {
                // @ts-expect-error $TSFixMeError: Argument of type 'ResizeObserverEntry[]' is not assignable to parameter of type 'NonEmptyArray<ResizeObserverEntry>'.
                const { contentRect } = nonEmptyArray.last(entries);
                const { height, width } = contentRect;
                // @ts-expect-error $TSFixMeError: Argument of type 'number' is not assignable to parameter of type 'SetStateAction<null>'.
                setAsideHeight(height);
                // @ts-expect-error $TSFixMeError: Argument of type 'number' is not assignable to parameter of type 'SetStateAction<null>'.
                setHeaderWidth(width);
            });
            if (bodyRef.current) {
                resizeObserver.observe(bodyRef.current);
            }
            return () => resizeObserver.disconnect();
        }, []);

        return (
            <ScrollSync debounced={debounceScrolling} debounceTimeout={debounceTimeout}>
                {({ onScroll, scrollLeft, scrollTop }) => (
                    <MatrixWrapper
                        // @ts-expect-error $TSFixMeError: No overload matches this call.
                        height={matrixHeight}
                        disableScrollSync={disableScrollSync}
                        width={matrixWidth}
                        data-test-matrix
                    >
                        <MatrixAside
                            cellHeight={cellHeight}
                            cornerCellHeight={headerHeight}
                            disableScrollSync={disableScrollSync}
                            expanded={expanded}
                            height={asideHeight}
                            ref={asideRef}
                            setExpanded={setExpanded}
                            scrollPosition={scrollTop}
                            width={asideWidth}
                            data-test-matrix-aside
                            {...asideProps}
                        >
                            {asideCells}
                        </MatrixAside>
                        {/* @ts-expect-error $TSFixMeError: No overload matches this call. */}
                        <MatrixContentWrapper disableScrollSync={disableScrollSync} data-test-matrix-content>
                            <MatrixHeader
                                cellWidth={cellWidth}
                                disableScrollSync={disableScrollSync}
                                height={headerHeight}
                                ref={headerRef}
                                scrollPosition={scrollLeft}
                                width={headerWidth}
                                data-test-matrix-header
                                {...headerProps}
                            >
                                {headerCells}
                            </MatrixHeader>
                            <MatrixBody
                                cellHeight={cellHeight}
                                cellWidth={cellWidth}
                                disableScrollSync={disableScrollSync}
                                expanded={expanded}
                                handleScroll={onScroll}
                                ref={bodyRef}
                                vectorDirection={vectorDirection}
                                data-test-matrix-body
                                {...bodyProps}
                            >
                                {vectors}
                            </MatrixBody>
                        </MatrixContentWrapper>
                    </MatrixWrapper>
                )}
            </ScrollSync>
        );
    };

    Matrix.defaultProps = {
        debounceScrolling: true,
        debounceTimeout: SCROLL_SYNC_DEBOUNCE_TIMEOUT,
    };

    Matrix.propTypes = {
        headerHeight: pt.number.isRequired,
        vectorDirection: pt.oneOf([VectorDirection.Row, VectorDirection.Column]).isRequired,
        vectors: pt.arrayOf(pt.node).isRequired,

        cellWidth: pt.oneOfType([pt.number, pt.arrayOf(pt.number)]),
        cellHeight: pt.oneOfType([pt.number, pt.arrayOf(pt.number)]),

        asideCells: pt.arrayOf(pt.node),
        asideProps: pt.shape({}),
        asideWidth: pt.number,
        bodyProps: pt.shape({}),
        debounceScrolling: pt.bool,
        debounceTimeout: pt.number,
        disableScrollSync: pt.bool,
        headerCells: pt.arrayOf(pt.node),
        headerProps: pt.shape({}),
        matrixHeight: pt.number,
        matrixWidth: pt.number,
        preExpandedVectorIndexes: pt.arrayOf(pt.string),
    };

    return Matrix;
};

export default matrixFactory();
